import { chartBands, pointTip, type ScoreHistory } from '../lib/scoreHistory.js';

/**
 * credifyfbs#129: the score table and trend for one scored form, shown at the top of the
 * client drawer's History popup. Same layout as the Form Builder's Score history: items
 * down the side, one column per fill (S1 = oldest), Total, Severity and Alert rows, then a
 * line of the totals when there are 2+ fills.
 */
const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};
const longDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** ehr#592: a fill scored on today's form (no score was saved with it). */
const CURRENT_TIP = 'Scored on the current form: no score was saved with this fill.';

export function ScoreHistoryTable({ h }: { h: ScoreHistory }) {
  const n = h.fills.length;
  const name = h.instrument?.name ?? 'Score';
  const onCurrent = h.fills.some((_, j) => h.basis[j] === 'current' && h.totals[j] != null);
  const anyOff = h.offVersion.some((r) => r.some(Boolean));
  return (
    <div className="sh-wrap-outer">
      <div className="sh-wrap">
        <table className="sh-table" style={{ minWidth: 304 + n * 80 }}>
          <colgroup>
            <col style={{ width: 34 }} />
            <col style={{ width: 270 }} />
            {h.fills.map((f) => (
              <col key={f.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="sh-num">#</th>
              <th className="sh-item">Item</th>
              {h.fills.map((f, i) => (
                <th
                  key={f.id}
                  title={`${longDate(f.at)} · ${f.by}${h.basis[i] === 'current' ? ` · ${CURRENT_TIP}` : ''}`}
                >
                  <div>S{i + 1}</div>
                  <div className="sh-date">{shortDate(f.at)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {h.items.map((it, ri) => (
              <tr key={it.id}>
                <td className="sh-num">{ri + 1}</td>
                <td className="sh-item">{it.label}</td>
                {h.fills.map((f, j) => {
                  const v = h.scores[ri]?.[j];
                  // ehr#592: this question wasn't on the form when this fill was filled
                  if (h.offVersion[ri]?.[j])
                    return (
                      <td
                        key={f.id}
                        className="sh-off"
                        title="Not on this version"
                        aria-label="Not on this version"
                      />
                    );
                  return (
                    <td key={f.id}>
                      {v == null ? (
                        <span className="sh-na" title="Not answered">
                          —
                        </span>
                      ) : (
                        v
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="sh-total">
              <td />
              <td className="sh-item">Total</td>
              {h.fills.map((f, j) => (
                <td key={f.id}>
                  {h.totals[j] ?? <span className="sh-na">—</span>}
                  {h.totals[j] != null && h.basis[j] === 'current' && (
                    <span className="sh-mark" title={CURRENT_TIP}>
                      *
                    </span>
                  )}
                </td>
              ))}
            </tr>
            {h.bands.some(Boolean) && (
              <tr className="sh-sev">
                <td />
                <td className="sh-item">Severity</td>
                {h.fills.map((f, j) => {
                  const b = h.bands[j];
                  return (
                    <td key={f.id}>
                      {b ? (
                        <span className={`sh-band sev-${b.severity}`}>{b.label}</span>
                      ) : (
                        <span className="sh-na">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            )}
            {h.alerts.some(Boolean) && (
              <tr className="sh-alertrow">
                <td />
                <td className="sh-item">Alert</td>
                {h.fills.map((f, j) => (
                  <td key={f.id}>
                    {h.alerts[j] && (
                      <span className="sh-alert" title={h.alerts[j]}>
                        ⚠ Flagged
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {(onCurrent || anyOff || h.hiddenAnswers > 0) && (
        <ul className="sh-notes">
          {onCurrent && <li>* Scored on the current form: no score was saved with that fill.</li>}
          {anyOff && <li>A blank cell: that question wasn't on the form when it was filled.</li>}
          {h.hiddenAnswers > 0 && (
            <li>
              {h.hiddenAnswers} answer{h.hiddenAnswers === 1 ? '' : 's'} not shown: not on the
              current form.
            </li>
          )}
        </ul>
      )}
      {n >= 2 && <ScoreTrend h={h} name={name} />}
    </div>
  );
}

/**
 * Totals by fill: a 2px line, ringed markers, the latest value labelled, band cut-offs dashed.
 * ehr#614: each severity band shaded behind the line and named, and a dot's hover is its score
 * and date only.
 */
function ScoreTrend({ h, name }: { h: ScoreHistory; name: string }) {
  const pts = h.totals
    .map((t, i) => ({ t, i }))
    .filter((p): p is { t: number; i: number } => p.t != null);
  if (pts.length < 2) return null;
  const W = 1000;
  const H = 200;
  const L = 34;
  const R = 46;
  const T = 14;
  const B = 30;
  const n = h.fills.length;
  const yMax = Math.max(h.maxTotal, ...pts.map((p) => p.t), 1);
  const pw = W - L - R;
  const ph = H - T - B;
  const x = (i: number) => L + (n === 1 ? pw / 2 : (pw * i) / (n - 1));
  const y = (v: number) => T + ph - (ph * v) / yMax;
  const cuts = [0, ...h.cutoffs.filter((c) => c > 0 && c < yMax), yMax];
  const zones = chartBands(h.instrument?.bands, yMax);
  const last = pts[pts.length - 1]!;
  return (
    <div className="sh-chart">
      <div className="sh-chart-title">{name} total by fill</div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${name} total by fill: ${pts.map((p) => p.t).join(', ')}`}
      >
        {zones.map((z) => {
          const top = y(z.to);
          const tall = y(z.from) - top;
          return (
            <g key={`z${z.from}`} className={`sh-zone sev-${z.severity}`}>
              <rect x={L} y={top} width={W - L - R} height={tall} />
              {tall >= 14 && (
                <text x={L + 8} y={y(z.from) - 5} fontSize={10.5} fontWeight={600}>
                  {z.label}
                </text>
              )}
            </g>
          );
        })}
        {cuts.map((c) => (
          <g key={c}>
            <line
              x1={L}
              x2={W - R}
              y1={y(c)}
              y2={y(c)}
              stroke="#e3ebe7"
              strokeWidth={1}
              strokeDasharray={c === 0 ? undefined : '3 4'}
            />
            <text x={L - 8} y={y(c)} dy={3.5} textAnchor="end" fontSize={10.5} fill="#8aaa9a">
              {c}
            </text>
          </g>
        ))}
        {h.fills.map((f, i) => (
          <text key={f.id} x={x(i)} y={H - 10} textAnchor="middle" fontSize={10.5} fill="#8aaa9a">
            S{i + 1}
          </text>
        ))}
        <polyline
          points={pts.map((p) => `${x(p.i).toFixed(1)},${y(p.t).toFixed(1)}`).join(' ')}
          fill="none"
          stroke="#1a8a66"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {pts.map((p) => {
          const f = h.fills[p.i]!;
          return (
            <g key={f.id}>
              <title>{pointTip(p.t, f.at)}</title>
              <circle cx={x(p.i)} cy={y(p.t)} r={12} fill="transparent" />
              <circle cx={x(p.i)} cy={y(p.t)} r={4} fill="#1a8a66" stroke="#fff" strokeWidth={2} />
            </g>
          );
        })}
        <text x={x(last.i) + 10} y={y(last.t)} dy={4} fontSize={12} fontWeight={700} fill="#0d1f18">
          {last.t}
        </text>
      </svg>
    </div>
  );
}
